import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { 
  Target, 
  Shield,
  Brain, 
  CheckCircle, 
  ArrowRight,
  AlertTriangle,
  BookOpen,
  TrendingUp,
  Clock,
  FileText,
  BarChart3,
  Zap,
  Users,
  GraduationCap,
  Newspaper,
  PenTool,
  XCircle
} from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();
  const isLoggedIn = Boolean(user);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="landing-page">
      
      {/* ========================================
          HERO SECTION - Decision Intelligence
          ======================================== */}
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Calm dark background — no foreground 3D, no motion. Copy does the work. */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />

        {/* Soft amber halo behind the headline to ground the brand color */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(251,146,60,0.35) 0%, rgba(251,146,60,0) 65%)' }}
          aria-hidden="true"
        />

        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 py-20 md:py-32 pointer-events-none">
          <div className="max-w-4xl mx-auto text-center space-y-8 pointer-events-auto">
            
            {/* Brand Badge */}
            <div className="inline-flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-full px-5 py-2.5">
              <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center">
                <Target className="w-4 h-4 text-white" />
              </div>
              <span className="text-slate-400 text-sm font-medium">Decision Intelligence for UPSC</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.15] text-white">
                UPSC is not a knowledge exam.
              </h1>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.15]">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">It is a decision exam.</span>
              </h1>
            </div>

            {/* Sub-headline */}
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Most aspirants don't fail because they didn't study.<br />
              They fail because of <span className="text-white font-medium">bad decisions under pressure</span> — over-attempting, CSAT panic, poor answer strategy.
            </p>

            {/* Value Prop */}
            <p className="text-xl md:text-2xl text-orange-400 font-semibold">
              CLARIAS trains decisions, not just content.
            </p>

            {/* CTA Button */}
            <div className="pt-6">
              <Button
                asChild
                size="lg"
                className="rounded-full px-10 py-7 text-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/25 group"
              >
                <Link to={isLoggedIn ? "/dashboard" : "/auth"} data-testid="get-started-btn">
                  Start Free
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Trust Signal */}
            <div className="pt-8 border-t border-slate-800 mt-8">
              <p className="text-slate-500 text-sm">
                Built for serious aspirants & repeaters.<br />
                No motivation. No false guarantees. Only exam-aligned intelligence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          WHY ASPIRANTS FAIL SECTION
          ======================================== */}
      <section className="py-20 bg-slate-900 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Why Intelligent Aspirants Still Fail UPSC
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { text: "You knew the syllabus — but over-attempted Prelims", icon: AlertTriangle },
              { text: "CSAT was \"qualifying\" — until it wasn't", icon: XCircle },
              { text: "You wrote answers — but marks didn't improve", icon: FileText },
              { text: "You studied more — but not what to skip", icon: Clock },
              { text: "Coaching gave content — not decision clarity", icon: BookOpen }
            ].map((problem, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-4 bg-slate-800/50 border border-slate-700 rounded-xl p-5 hover:border-orange-500/50 transition-colors"
              >
                <problem.icon className="w-6 h-6 text-red-400 flex-shrink-0" />
                <p className="text-slate-300">{problem.text}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center pt-8">
            <div className="inline-block bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-2xl">
              <p className="text-xl md:text-2xl text-slate-300 leading-relaxed">
                UPSC punishes optimism.<br />
                <span className="text-orange-400 font-bold">It rewards restraint.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          WHAT CLARIAS IS SECTION
          ======================================== */}
      <section className="py-20 bg-slate-950 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 text-sm text-orange-400 font-medium">
              <Brain className="w-4 h-4" />
              What CLARIAS Actually Is
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              A Decision-Intelligence System for UPSC
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              CLARIAS is <span className="text-white font-medium">not another content platform</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { text: "How you attempt questions", icon: Target },
              { text: "Where you lose marks", icon: TrendingUp },
              { text: "Why CSAT becomes risky", icon: AlertTriangle },
              { text: "Which topics deserve time", icon: Clock },
              { text: "What not to do in the exam", icon: Shield },
              { text: "Clear exam-day decisions", icon: Zap }
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-4 bg-slate-900 border border-slate-800 rounded-xl p-5"
              >
                <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-orange-400" />
                </div>
                <p className="text-slate-300">{item.text}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-400 text-lg">
            We convert <span className="text-white">PYQs, current affairs, and your behaviour</span> into clear exam-day decisions.
          </p>
        </div>
      </section>

      {/* ========================================
          PRELIMS SECTION
          ======================================== */}
      <section className="py-20 bg-slate-900 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2 text-sm text-blue-400 font-medium">
              <Shield className="w-4 h-4" />
              Prelims First — The Entry Gate
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              First, we stop you from failing Prelims
            </h2>
            <p className="text-slate-400">
              Because without clearing Prelims, everything else is irrelevant.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { 
                title: "30-Year PYQ Intelligence", 
                desc: "What UPSC really tests — pattern analysis from 1995-2024",
                icon: BarChart3
              },
              { 
                title: "Current Affairs → MCQ Intelligence", 
                desc: "Application, not recall — convert news into practice",
                icon: Newspaper
              },
              { 
                title: "Trap Detection System", 
                desc: "Identify deceptive questions before they cost you marks",
                icon: AlertTriangle
              },
              { 
                title: "Prelims Intensive (60 Tests)", 
                desc: "30 GS + 30 CSAT tests — correcting behaviour, not inflating scores",
                icon: Target
              }
            ].map((feature, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700 hover:border-blue-500/50 transition-colors">
                <CardContent className="p-6 space-y-3">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Editorial Analyzer Feature - Highlighted */}
          <Link to="/editorial-analyzer" className="block">
            <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/30 hover:border-orange-500/60 transition-all cursor-pointer group">
              <CardContent className="p-6">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-white">Editorial Analyzer</h3>
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full">NEW</span>
                    </div>
                    <p className="text-slate-300">
                      Paste any newspaper editorial → Get complete UPSC analysis
                    </p>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {["Summaries", "GS Mapping", "Mains Q&A", "5 MCQs", "PDF Export"].map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-800 text-slate-400 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ArrowRight className="w-6 h-6 text-orange-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <div className="text-center">
            <p className="text-slate-500 italic">
              CLARIAS doesn't push you to attempt more.<br />
              <span className="text-blue-400">It trains you to attempt safely.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          CSAT SECTION
          ======================================== */}
      <section className="py-20 bg-slate-950 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-2 text-sm text-red-400 font-medium">
              <AlertTriangle className="w-4 h-4" />
              CSAT — The Silent Killer
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              CSAT is qualifying. Failure is catastrophic.
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              CLARIAS treats CSAT as a <span className="text-white font-medium">risk-management problem</span>, not a math contest.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Diagnostic Assessment", desc: "Know your CSAT baseline", icon: BarChart3 },
              { title: "Topic-wise Practice", desc: "RC, Quant, Reasoning, DI", icon: BookOpen },
              { title: "Mock Tests", desc: "Full-length CSAT simulations", icon: FileText }
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center space-y-3">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mx-auto">
                  <item.icon className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="font-semibold text-white">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-slate-500 italic">
              CSAT should feel boring.<br />
              <span className="text-red-400">Boring clears CSAT.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          MAINS SECTION
          ======================================== */}
      <section className="py-20 bg-slate-900 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 text-sm text-emerald-400 font-medium">
              <PenTool className="w-4 h-4" />
              Mains — After Survival
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Convert Prelims clearance into rank
            </h2>
            <p className="text-slate-400">
              Once Prelims is safe, CLARIAS shifts to Mains performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "GS I–IV answer writing",
              "Essay practice & evaluation",
              "Ethics case simulations",
              "Rubric-based answer evaluation",
              "Editorial Analyzer — convert news to answers",
              "PYQ-anchored trend-based questions"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300">{item}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-slate-500">
              Not generic feedback. <span className="text-emerald-400 font-medium">Examiner-style correction.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          OPTIONALS SECTION
          ======================================== */}
      <section className="py-20 bg-slate-950 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2 text-sm text-purple-400 font-medium">
              <GraduationCap className="w-4 h-4" />
              Optionals — Rank Multiplier
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Optional subjects decide rank
            </h2>
            <p className="text-slate-400">CLARIAS treats them seriously.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {["Sociology", "Anthropology", "Public Administration", "PSIR"].map((subject, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center hover:border-purple-500/50 transition-colors">
                <h3 className="font-semibold text-white mb-3">{subject}</h3>
                <p className="text-slate-500 text-sm">Paper I & II</p>
              </div>
            ))}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <p className="text-slate-400 text-center mb-4">Each optional includes:</p>
            <div className="grid md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {[
                "Paper I & II separated engines",
                "30-year PYQ analysis",
                "Trend-Based Questions",
                "Answer writing & evaluation"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          CURRENT AFFAIRS SECTION
          ======================================== */}
      <section className="py-20 bg-slate-900 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2 text-sm text-amber-400 font-medium">
              <Newspaper className="w-4 h-4" />
              Daily Current Affairs
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Current Affairs without overload
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { text: "Filters daily news", icon: Zap },
              { text: "Maps it to syllabus", icon: Target },
              { text: "Shows Prelims & Mains angles", icon: BookOpen },
              { text: "Highlights what actually matters", icon: TrendingUp }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-slate-300">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-slate-500">
              No memorisation. <span className="text-amber-400">Only application.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          PRICING SECTION
          ======================================== */}
      <section className="py-20 bg-slate-950 relative">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Coaching costs ₹50,000+
            </h2>
            <p className="text-xl text-slate-400">
              CLARIAS costs less than one mistake.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white">FREE</h3>
                  <p className="text-slate-500 text-sm mt-1">Explore decision intelligence</p>
                </div>
                <div className="text-3xl font-bold text-white">₹0</div>
                <ul className="space-y-3">
                  {[
                    "Sample PYQ analysis",
                    "Limited practice",
                    "Basic current affairs"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-slate-600" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button asChild variant="outline" className="w-full rounded-full border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Link to="/auth">Start Free</Link>
                </Button>
              </CardContent>
            </Card>

            {/* PRO */}
            <Card className="bg-gradient-to-b from-orange-500/10 to-slate-900 border-orange-500/50 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                RECOMMENDED
              </div>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white">CLARIAS PRO</h3>
                  <p className="text-slate-400 text-sm mt-1">Complete Prelims + Mains system</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-500 line-through">₹23,999</span>
                  <span className="text-3xl font-bold text-white">₹6,999</span>
                </div>
                <p className="text-xs text-emerald-400 font-medium -mt-2">Founding pricing • Save ₹17,000</p>
                <ul className="space-y-3">
                  {[
                    "Full Prelims Intelligence",
                    "Complete CSAT Suite",
                    "GS I–IV + Essay",
                    "Rubric Answer Evaluation",
                    "Valid till Mains 2027 (2 attempts)"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-orange-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full rounded-full bg-orange-500 hover:bg-orange-600 text-white">
                  <Link to="/auth?upgrade=true">Unlock PRO</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Optionals */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white">Optionals</h3>
                  <p className="text-slate-500 text-sm mt-1">Dedicated Paper I & II engines</p>
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-slate-500 line-through text-lg">₹5,999</span>
                  <span className="text-3xl font-bold text-white">₹1,999</span>
                  <span className="text-lg text-slate-500 font-normal">each</span>
                </div>
                <ul className="space-y-3">
                  {[
                    "Sociology",
                    "Anthropology",
                    "Public Administration",
                    "PSIR"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button asChild variant="outline" className="w-full rounded-full border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Link to="/auth">View Optionals</Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <p className="text-slate-500 italic">
              No guarantees. No false promises.<br />
              <span className="text-slate-400">Just reduced failure probability.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          WHO SHOULD USE SECTION
          ======================================== */}
      <section className="py-20 bg-slate-900 relative">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Who Should Use CLARIAS
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* For You */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-emerald-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                CLARIAS is for you if:
              </h3>
              <ul className="space-y-3">
                {[
                  "You study seriously but fear repeating failure",
                  "You've faced Prelims before",
                  "You want clarity, not motivation",
                  "You value restraint over bravado",
                  "You want a system — not a guru"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Not For */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-red-400 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Not for:
              </h3>
              <ul className="space-y-3">
                {[
                  "Casual aspirants",
                  "Shortcut seekers"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-400">
                    <XCircle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          FINAL CTA SECTION
          ======================================== */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 to-transparent" />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              UPSC punishes mistakes.
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">CLARIAS helps you avoid them.</span>
            </h2>
          </div>
          
          <div className="pt-6">
            <Button 
              asChild 
              size="lg" 
              className="rounded-full px-12 py-8 text-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/25"
            >
              <Link to="/auth">
                Start Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ========================================
          FOOTER
          ======================================== */}
      <footer className="py-12 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center">
                <Target className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-white">CLARIAS</span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-500 text-sm">Decision Intelligence for UPSC</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/privacy-policy" className="hover:text-slate-400">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-slate-400">Terms</Link>
              <Link to="/refund-policy" className="hover:text-slate-400">Refunds</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <p className="text-sm text-slate-600">
              © 2025 CLARIAS. Built for aspirants who value decisions over noise.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
